import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Activity, CheckCircle, AlertCircle, Clock, Menu, X } from 'lucide-react';
import './InfoPage.css';

const StatusPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const services = [
    { name: 'API Services', status: 'operational', uptime: '99.99%' },
    { name: 'CAD Processing', status: 'operational', uptime: '99.97%' },
    { name: 'Site Analysis (GIS)', status: 'operational', uptime: '99.95%' },
    { name: 'AI Communications', status: 'operational', uptime: '99.98%' },
    { name: 'Vendor Bidding System', status: 'operational', uptime: '99.96%' },
    { name: 'Dashboard & Web App', status: 'operational', uptime: '99.99%' },
    { name: 'File Storage', status: 'operational', uptime: '100%' },
    { name: 'Email Services', status: 'operational', uptime: '99.98%' }
  ];

  const recentIncidents = [
    {
      date: 'October 13, 2025',
      title: 'All Systems Operational',
      description: 'No incidents reported. All services running smoothly.',
      status: 'resolved'
    }
  ];

  const getStatusIcon = (status: string) => {
    if (status === 'operational') return <CheckCircle style={{ color: '#10B981', width: '20px', height: '20px' }} />;
    if (status === 'degraded') return <AlertCircle style={{ color: '#F59E0B', width: '20px', height: '20px' }} />;
    return <AlertCircle style={{ color: '#EF4444', width: '20px', height: '20px' }} />;
  };

  const getStatusText = (status: string) => {
    if (status === 'operational') return { text: 'Operational', color: '#10B981' };
    if (status === 'degraded') return { text: 'Degraded Performance', color: '#F59E0B' };
    return { text: 'Outage', color: '#EF4444' };
  };

  return (
    <div className="info-page">
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="info-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a onClick={() => navigate('/features')}>Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">Sign In</button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">Start Free Trial</button>
          </div>
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      <section className="info-hero">
        <div className="hero-container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="hero-badge">
              <Activity className="badge-icon" />
              <span>SYSTEM STATUS</span>
            </div>
            <h1 className="hero-title">
              All Systems <span className="gradient-text">Operational</span>
            </h1>
            <p className="hero-subtitle">
              Real-time status of HomeQuest Tech platform and services.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="info-content">
        <div className="content-container">
          <div style={{ padding: '2rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '16px', border: '1px solid rgba(16, 185, 129, 0.3)', marginBottom: '3rem', textAlign: 'center' }}>
            <CheckCircle style={{ width: '48px', height: '48px', color: '#10B981', margin: '0 auto 1rem' }} />
            <h2 style={{ color: '#10B981', marginBottom: '0.5rem' }}>All Systems Operational</h2>
            <p style={{ color: '#D1D5DB', margin: 0 }}>Last checked: Just now</p>
          </div>

          <h2>Service Status</h2>
          <div style={{ marginBottom: '3rem' }}>
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1.5rem',
                  background: 'rgba(31, 41, 55, 0.6)',
                  borderRadius: '12px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  marginBottom: '1rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {getStatusIcon(service.status)}
                  <span style={{ fontSize: '1.125rem', color: '#F9FAFB', fontWeight: '500' }}>{service.name}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: getStatusText(service.status).color, fontWeight: '600', marginBottom: '0.25rem' }}>
                    {getStatusText(service.status).text}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>
                    {service.uptime} uptime
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <h2>Recent Incidents</h2>
          <div>
            {recentIncidents.map((incident, index) => (
              <div
                key={index}
                style={{
                  padding: '1.5rem',
                  background: 'rgba(31, 41, 55, 0.6)',
                  borderRadius: '12px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  marginBottom: '1rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Clock style={{ width: '16px', height: '16px', color: '#9CA3AF' }} />
                  <span style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>{incident.date}</span>
                </div>
                <h3 style={{ color: '#F9FAFB', marginBottom: '0.5rem' }}>{incident.title}</h3>
                <p style={{ color: '#D1D5DB', margin: 0 }}>{incident.description}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '3rem', padding: '2rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '16px', border: '1px solid rgba(59, 130, 246, 0.3)', textAlign: 'center' }}>
            <h3 style={{ color: '#F9FAFB', marginBottom: '1rem' }}>Stay Updated</h3>
            <p style={{ color: '#D1D5DB', marginBottom: '1.5rem' }}>
              Subscribe to status updates or contact support if you're experiencing issues.
            </p>
            <button onClick={() => navigate('/contact')} className="cta-button">
              Contact Support
            </button>
          </div>
        </div>
      </section>

      <footer className="info-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/features')}>Features</a>
              <a onClick={() => navigate('/help')}>Help Center</a>
              <a onClick={() => navigate('/contact')}>Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StatusPage;
